// H-003 — historial de versiones de UN recurso (Medplum `_history` + Provenance), con autor y sello temporal. PURO: sin red ni React.
//
// De dónde sale cada dato:
//   - versión y fecha: `meta.versionId` y `meta.lastUpdated` de cada entrada del Bundle `history` (hora del SERVIDOR de Medplum, no del navegador). Una entrada
//     de borrado no trae recurso: su fecha sale de `response.lastModified`.
//   - autor: primero un Provenance cuyo `target` apunta a ESA versión (`Tipo/id/_history/v`); si el Provenance no nombra versión, se asigna a la última versión
//     guardada hasta su `recorded`. Si no hay Provenance, `meta.author` (Medplum lo pone en cada versión con la cuenta que escribió). Si nada, `null`.
//   - qué cambió: solo los NOMBRES de los campos de primer nivel que difieren entre versiones consecutivas, nunca sus valores. Así la vista no muestra ningún
//     dato clínico de una versión anterior: lo que el rol puede ver del contenido es lo que ya ve en la pantalla del recurso actual.
import type { Bundle, Provenance, Reference, Resource } from '@medplum/fhirtypes';

export interface VersionRecurso {
  versionId: string;
  /** ISO 8601 (servidor). `null` si Medplum no la informó. */
  fecha: string | null;
  autor: string | null;
  /** Motivo declarado en el Provenance de esa versión (adenda/corrección), si lo hay. */
  motivo: string | null;
  eliminado: boolean;
  /** Es la primera versión del recurso (solo si el historial se cargó COMPLETO). */
  creacion: boolean;
  /** Campos de primer nivel que cambiaron respecto de la versión anterior. `null` en la versión más vieja cargada (no hay con qué comparar). */
  camposCambiados: string[] | null;
}

/** ¿El Bundle (de `_history` o de búsqueda) anuncia una página siguiente? */
export const tieneSiguiente = (b: Bundle | undefined): boolean => (b?.link ?? []).some((l) => l.relation === 'next' && !!l.url);

/** Campos que cambian en cada guardado sin decir nada clínico (o que son el propio identificador): no cuentan como «cambio». */
const CAMPOS_IGNORADOS = new Set(['meta', 'id', 'resourceType', 'text']);

/** Igualdad estructural de valores JSON (el orden de las claves de un objeto no importa; el de los arreglos sí, como en FHIR). */
export function igualesJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    const bb = b as unknown[];
    return a.length === bb.length && a.every((x, i) => igualesJson(x, bb[i]));
  }
  const oa = a as Record<string, unknown>;
  const ob = b as Record<string, unknown>;
  const claves = new Set([...Object.keys(oa), ...Object.keys(ob)]);
  for (const k of claves) if (!igualesJson(oa[k], ob[k])) return false;
  return true;
}

/** Nombres (ordenados) de los campos de primer nivel que difieren entre dos versiones; agregados, quitados o modificados. Sin valores. */
export function camposCambiados(anterior: Resource | undefined, actual: Resource | undefined): string[] {
  const a = (anterior ?? {}) as Record<string, unknown>;
  const b = (actual ?? {}) as Record<string, unknown>;
  const claves = new Set([...Object.keys(a), ...Object.keys(b)]);
  const salida: string[] = [];
  for (const k of claves) {
    if (CAMPOS_IGNORADOS.has(k)) continue;
    if (!igualesJson(a[k], b[k])) salida.push(k);
  }
  return salida.sort();
}

const REF_CON_VERSION = /^(?:.*\/)?([A-Z][A-Za-z]+)\/([A-Za-z0-9\-.]{1,64})(?:\/_history\/([A-Za-z0-9\-.]{1,64}))?$/;

/** `{ tipo, id, version }` de una referencia relativa o absoluta, con o sin `_history`. */
export function desarmarReferencia(ref: string | undefined): { tipo: string; id: string; version: string | null } | null {
  const m = ref ? REF_CON_VERSION.exec(ref) : null;
  return m ? { tipo: m[1]!, id: m[2]!, version: m[3] ?? null } : null;
}

const textoDeReferencia = (r: Reference | undefined): string | null => r?.display?.trim() || r?.reference || null;

function autorDeProvenance(p: Provenance): string | null {
  for (const a of p.agent ?? []) {
    const t = textoDeReferencia(a.who) ?? textoDeReferencia(a.onBehalfOf);
    if (t) return t;
  }
  return null;
}

const motivoDeProvenance = (p: Provenance): string | null => p.reason?.[0]?.text?.trim() || p.reason?.[0]?.coding?.[0]?.display || p.activity?.text?.trim() || null;

const tiempo = (iso: string | null | undefined): number => {
  const t = Date.parse(iso ?? '');
  return Number.isFinite(t) ? t : Number.NaN;
};

/**
 * Versiones del recurso `tipo/id`, de la más NUEVA a la más vieja, a partir del Bundle de `_history` y de los Provenance que lo apuntan.
 * Las entradas de otro recurso (no debería haberlas) se descartan.
 */
export function versionesDeHistorial(
  tipo: string,
  id: string,
  /** Una página o varias, en el orden en que llegaron (cada una de la más nueva a la más vieja). */
  historial: Bundle | Bundle[] | undefined,
  provenances: Provenance[] = [],
  /** `false` si quedaron versiones más viejas sin cargar (`link next` tras el tope): la más vieja mostrada NO es la creación. */
  opciones: { completo?: boolean } = {},
): VersionRecurso[] {
  const completo = opciones.completo ?? true;
  type Cruda = { versionId: string; fecha: string | null; recurso: Resource | undefined; eliminado: boolean; autorMeta: string | null };
  const crudas: Cruda[] = [];
  const paginas = Array.isArray(historial) ? historial : historial ? [historial] : [];
  for (const e of paginas.flatMap((b) => b.entry ?? [])) {
    const r = e.resource;
    if (r) {
      if (r.resourceType !== tipo || r.id !== id) continue;
      const versionId = r.meta?.versionId;
      if (!versionId) continue;
      crudas.push({ versionId, fecha: r.meta?.lastUpdated ?? null, recurso: r, eliminado: false, autorMeta: textoDeReferencia(r.meta?.author) });
    } else if (e.request?.method === 'DELETE' || e.response?.status?.startsWith('410')) {
      const ref = desarmarReferencia(e.request?.url ?? e.fullUrl ?? e.response?.location);
      if (ref && (ref.tipo !== tipo || ref.id !== id)) continue;
      const etag = /W\/"([^"]+)"/.exec(e.response?.etag ?? '')?.[1] ?? ref?.version ?? null;
      crudas.push({ versionId: etag ?? '—', fecha: e.response?.lastModified ?? null, recurso: undefined, eliminado: true, autorMeta: null });
    }
  }
  // De la más vieja a la más nueva para comparar cada una con la anterior. Medplum entrega el historial de la más nueva a la más vieja: se invierte ese orden y
  // luego se ordena por fecha con un sort ESTABLE, así un empate de fecha conserva el orden del servidor (los versionId son UUID: no dicen nada del orden).
  // Si alguna entrada no trae fecha, no hay criterio temporal fiable y se usa solo el orden del servidor.
  crudas.reverse();
  if (crudas.every((c) => !Number.isNaN(tiempo(c.fecha)))) crudas.sort((x, y) => tiempo(x.fecha) - tiempo(y.fecha));

  // Provenance → versión: explícita por `_history/v`; si no, la última versión guardada hasta `recorded`.
  const porVersion = new Map<string, Provenance>();
  for (const p of provenances) {
    for (const t of p.target ?? []) {
      const ref = desarmarReferencia(t.reference);
      if (!ref || ref.tipo !== tipo || ref.id !== id) continue;
      let version = ref.version;
      if (!version) {
        const recorded = tiempo(p.recorded);
        if (Number.isNaN(recorded)) continue;
        const candidatas = crudas.filter((c) => !Number.isNaN(tiempo(c.fecha)) && tiempo(c.fecha) <= recorded);
        version = candidatas.at(-1)?.versionId ?? null;
      }
      if (version && !porVersion.has(version)) porVersion.set(version, p);
    }
  }

  const salida: VersionRecurso[] = [];
  let previo: Resource | undefined;
  crudas.forEach((c, i) => {
    const prov = porVersion.get(c.versionId);
    salida.push({
      versionId: c.versionId,
      fecha: c.fecha,
      autor: (prov && autorDeProvenance(prov)) ?? c.autorMeta,
      motivo: prov ? motivoDeProvenance(prov) : null,
      eliminado: c.eliminado,
      creacion: i === 0 && completo,
      camposCambiados: i === 0 ? null : c.eliminado ? [] : camposCambiados(previo, c.recurso),
    });
    if (c.recurso) previo = c.recurso;
  });
  return salida.reverse();
}
